/* eslint-disable sort-imports */
import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { createPortal } from 'react-dom';
import { IAdminBulletinItem } from 'src/services/models/bulletins.service';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import {
    BulletinSortColumn,
    BulletinSortDirection,
    formatDateOnly,
    getBulletinLanguageName,
    getBulletinTypeName,
    isBulletinExpirable,
} from 'src/utils/bulletins';

import './style.scss';

export interface IBulletinsAdminTableProps {
    items: IAdminBulletinItem[];
    sortColumn: BulletinSortColumn;
    sortDirection: BulletinSortDirection;
    onSort: (column: BulletinSortColumn) => void;
    onSelectBulletin: (item: IAdminBulletinItem) => void;
    onExpire?: (item: IAdminBulletinItem) => void;
    onDelete?: (item: IAdminBulletinItem) => void;
}

const CHEVRON_COLOR = '#363b49';
const CHEVRON_WIDTH = 10;
const MENU_HEIGHT_ESTIMATE = 90;

interface IMenuPosition {
    top?: number;
    bottom?: number;
    right: number;
}

export const BulletinActionsMenu: React.FC<{
    item: IAdminBulletinItem;
    onExpire?: (item: IAdminBulletinItem) => void;
    onDelete?: (item: IAdminBulletinItem) => void;
    variant?: 'inline' | 'circle';
}> = ({ item, onExpire, onDelete, variant = 'inline' }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<IMenuPosition>({
        right: 0,
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.ViewBulletinPage.${key}`, { defaultValue });

    useEffect(() => {
        if (!isOpen) return undefined;

        const close = () => setIsOpen(false);
        const closeOnOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;

            if (
                !containerRef.current?.contains(target) &&
                !menuRef.current?.contains(target)
            ) {
                close();
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };

        menuRef.current
            ?.querySelector<HTMLButtonElement>('button:not(:disabled)')
            ?.focus({ preventScroll: true });

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        // The menu is fixed to the viewport, so it must not outlive a scroll.
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [isOpen]);

    const toggleMenu = () => {
        if (!isOpen && toggleRef.current) {
            const rect = toggleRef.current.getBoundingClientRect();
            const { clientHeight, clientWidth } = document.documentElement;
            const opensUp = rect.bottom + MENU_HEIGHT_ESTIMATE > clientHeight;

            setMenuPosition({
                right: clientWidth - rect.right,
                ...(opensUp
                    ? { bottom: clientHeight - rect.top }
                    : { top: rect.bottom }),
            });
        }

        setIsOpen((current) => !current);
    };

    const runAction = (action?: (item: IAdminBulletinItem) => void) => {
        setIsOpen(false);
        action?.(item);
    };

    return (
        <div className='bulletins-admin-actions' ref={containerRef}>
            <button
                ref={toggleRef}
                className={`bulletins-admin-actions__toggle${
                    variant === 'circle'
                        ? ' bulletins-admin-actions__toggle--circle'
                        : ''
                }`}
                type='button'
                aria-label={text('Actions', 'Actions')}
                aria-haspopup='menu'
                aria-expanded={isOpen}
                onClick={toggleMenu}
            >
                <span aria-hidden='true'>&middot;&middot;&middot;</span>
            </button>
            {isOpen &&
                createPortal(
                    <div
                        className='bulletins-admin-actions__menu'
                        role='menu'
                        ref={menuRef}
                        style={menuPosition}
                    >
                        <button
                            className='bulletins-admin-actions__item'
                            type='button'
                            role='menuitem'
                            disabled={
                                !onExpire ||
                                !isBulletinExpirable(item.validThrough)
                            }
                            onClick={() => runAction(onExpire)}
                        >
                            {text('ExpireBulletin', 'Expire Bulletin')}
                        </button>
                        <button
                            className='bulletins-admin-actions__item bulletins-admin-actions__item--danger'
                            type='button'
                            role='menuitem'
                            disabled={!onDelete}
                            onClick={() => runAction(onDelete)}
                        >
                            {text('DeleteBulletin', 'Delete Bulletin')}
                        </button>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export const BulletinsAdminTable: React.FC<IBulletinsAdminTableProps> = ({
    items,
    sortColumn,
    sortDirection,
    onSort,
    onSelectBulletin,
    onExpire,
    onDelete,
}) => {
    const { t } = useTranslation();
    const text = (key: string, defaultValue: string, column?: string) =>
        t(`SystemAdmin.ViewBulletinPage.${key}`, { defaultValue, column });

    const renderSortableHeader = (
        column: BulletinSortColumn,
        label: string,
    ) => {
        const isActive = sortColumn === column;
        const isAscending = isActive && sortDirection === 'asc';

        return (
            <div
                role='columnheader'
                aria-sort={
                    isActive
                        ? isAscending
                            ? 'ascending'
                            : 'descending'
                        : 'none'
                }
            >
                <span>{label}</span>
                <button
                    className={`bulletins-admin-table__sort${
                        isActive ? '' : ' is-inactive'
                    }`}
                    type='button'
                    onClick={() => onSort(column)}
                    aria-label={
                        isAscending
                            ? text(
                                  'SortDescending',
                                  'Sort {{column}} descending',
                                  label,
                              )
                            : text(
                                  'SortAscending',
                                  'Sort {{column}} ascending',
                                  label,
                              )
                    }
                >
                    <Chevron
                        className={`accordion__icon ${
                            isAscending || !isActive
                                ? 'rotate-down'
                                : 'rotate-up'
                        }`}
                        width={CHEVRON_WIDTH}
                        fill={CHEVRON_COLOR}
                    />
                </button>
            </div>
        );
    };

    return (
        <div className='bulletins-admin-table' role='table'>
            <div className='bulletins-admin-table__row is-header' role='row'>
                <div role='columnheader'>
                    {text('ColumnNumber', 'Bulletin #')}
                </div>
                <div role='columnheader'>{text('ColumnTitle', 'Title')}</div>
                {renderSortableHeader('type', text('ColumnType', 'Type'))}
                {renderSortableHeader('rule', text('ColumnRule', 'Rule'))}
                {renderSortableHeader(
                    'language',
                    text('ColumnLanguage', 'Language'),
                )}
                {renderSortableHeader(
                    'startDate',
                    text('ColumnStartDate', 'Start Date'),
                )}
                {renderSortableHeader(
                    'endDate',
                    text('ColumnEndDate', 'End Date'),
                )}
                {renderSortableHeader(
                    'modifyDate',
                    text('ColumnModifyDate', 'Modify Date'),
                )}
                <div role='columnheader'>
                    {text('ColumnActions', 'Actions')}
                </div>
            </div>

            {items.map((item) => (
                <div
                    className='bulletins-admin-table__row'
                    role='row'
                    key={item.bulletinId || item.documentId}
                >
                    <div role='cell'>
                        <button
                            className='bulletins-admin-table__link'
                            type='button'
                            onClick={() => onSelectBulletin(item)}
                        >
                            {item.bulletinNumber}
                        </button>
                    </div>
                    <div role='cell'>{item.title}</div>
                    <div role='cell'>
                        {getBulletinTypeName(item.bulletinType)}
                    </div>
                    <div role='cell'>{item.distributionName ?? ''}</div>
                    <div role='cell'>
                        {getBulletinLanguageName(item.bulletinLanguage) ===
                        'french'
                            ? text('French', 'French')
                            : text('English', 'English')}
                    </div>
                    <div role='cell'>{formatDateOnly(item.validFrom)}</div>
                    <div role='cell'>{formatDateOnly(item.validThrough)}</div>
                    <div role='cell'>{formatDateOnly(item.modifytime)}</div>
                    <div role='cell'>
                        <BulletinActionsMenu
                            item={item}
                            onExpire={onExpire}
                            onDelete={onDelete}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default BulletinsAdminTable;
